import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
	useRef,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { CssPosition, GreenroomState as GS } from '../../stores/types/types';
import { ROOM_STATES } from '../../stores/reducers/settings';
import {
	FEED_TYPES, PLAY_STATES, Shot, ShotFeed, SHOT_THUMBNAIL_TYPES,
} from '../../stores/reducers/shots';
import {
	toMap,
	numberToPercent,
	percMultiply,
	getFeedStyle,
	isVideo,
} from '../../utils/utils';
import { FIXED_LAYOUTS, PLAIN_LAYOUTS } from '../../utils/video-layout';
import Icon, { ICONS, COLORS } from './icon';
import Title from '../main-video/title';
import FeedThumbnail from './feed-thumbnail';
import CustomGraphicsCanvas, { CanvasLocation } from '../custom-graphics/custom-graphics-canvas';
import { videoGridClasses } from '../../types/constants';
import { DebugGate } from '../debug-views/debug-panel/debug-gate';
import { useVideoMachine } from '../../contexts/video-machine';
import ShotContextMenu from '../context-menu/shot-context-menu';
import Portal from './portal';
import useShots from '../../contexts/shots/provider';
import Socket from '../../connection/socket';
import { GenerateShotThumbnail } from '../../connection/shots';

interface Props {
	open?: boolean;
	selected?: boolean;
	playStart?: Date | null;
	playState?: number | null;
	isDragging?: boolean;
	borderRatio: number;
	shot: Shot;
	onSelect?: (shotId: number) => void;
	onPlay?: (shotId: number) => void;
	shiftKeyDepressed?: boolean;
	missingFeeds?: string;
	onTooltipHover?: (
		hover: boolean,
		missingFeeds: string,
		elRef: HTMLButtonElement | HTMLDivElement,
	) => void;
	shotThumbnailType: number;
	onEditClicked?: (shotId: number) => void;
	socket?: Socket | null;
}

export default function ShotThumbnail(props: Props) {
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const clearShot = useSelector((s: GS) => s.SettingsReducer.clearShot);
	const canvasStyle = useSelector((s: GS) => s.ShotsReducer.canvasStyle);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const { debugOverlaysEnabled } = useSelector((s: GS) => s.DebugReducer);
	const {
		use_new_shot_thumbs: useNewShotThumbs,
	} = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const {
		open = false,
		selected = false,
		playStart = null,
		playState = null,
		isDragging = false,
		borderRatio,
		shot,
		onSelect,
		onPlay,
		shiftKeyDepressed = false,
		missingFeeds = '',
		onTooltipHover,
		shotThumbnailType,
		onEditClicked,
		socket,
	} = props;

	const {
		shot_id: shotId,
		shot_data: {
			forcedLayout,
			shotFeeds,
			shotDuration,
			videoTitlesOn,
			order,
			shotGraphics,
			autoplay,
		},
	} = shot;

	const {
		backgroundType,
		backgroundImage,
		backgroundColor,
		backgroundScale,
		layoutSpacingPresets,
		presenterVideoBorderPosition,
		presenterVideoBorderSize,
		presenterVideoBorderColor,
		presenterVideoRadius,
		presenterVideoBorderOn,
		presenterVideoBorderOpacity,
	} = canvasStyle;

	const playing = selected && playStart !== null && !clearShot;

	const shotButtonRef = useRef<HTMLButtonElement | null>(null);
	const thumbnailRef = useRef<HTMLDivElement | null>(null);
	const menuDotsRef = useRef<HTMLDivElement | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const pauseTimeout = useRef<NodeJS.Timeout>();

	const [isPlayingDelay, setIsPlayingDelay] = useState<number>(0);
	const [animationHasEnded, setAnimationHasEnded] = useState<boolean>(true);
	const [hovering, setHovering] = useState<boolean>(false);
	const [showingTooltip, setShowingTooltip] = useState<boolean>(false);
	const [thumbnailWidth, setThumbnailWidth] = useState<number>(0);
	const [durationBarLeft, setDurationBarLeft] = useState<number>(0);
	const [showMenu, setShowMenu] = useState<boolean>(false);
	const [menuPosition, setMenuPosition] = useState<CssPosition>({top: 0, left: 0});
	const standardFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => !shotFeed.audioOnly)
		: []), [shotFeeds]);

	const { videoMachineState } = useVideoMachine();

	// Determine if we should show the thumbnail overlay for showboard
	const showThumbnailOverlay = useMemo(
		() => useNewShotThumbs
			&& shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOWBOARD
			&& shot.thumbnail_url
			&& shot.thumbnail_url !== 'generating',
		[useNewShotThumbs, shotThumbnailType, shot.thumbnail_url],
	);

	const isThumbnailVideo = useMemo(
		() => isVideo(shot.thumbnail_url ?? ''),
		[shot.thumbnail_url],
	);
	
	// Generate thumbnail URL with cache buster, always a new v because stylepack edits
	// doesn't update the edited field
	const thumbnailUrl = useMemo(
		() => `${shot.thumbnail_url}?v=${new Date().getTime()}`,
		[shot.thumbnail_url, shot.edited],
	);

	// Handle video hover play/pause
	const handleThumbnailMouseEnter = useCallback(() => {
		clearTimeout(pauseTimeout.current);
		if (showThumbnailOverlay && isThumbnailVideo && videoRef.current) {
			videoRef.current.play().catch((err) => {
				console.warn('Video play failed:', err);
			});
		}
	}, [showThumbnailOverlay, isThumbnailVideo]);

	const handleThumbnailMouseLeave = useCallback(() => {
		pauseTimeout.current = setTimeout(() => {
			if (showThumbnailOverlay && isThumbnailVideo && videoRef.current) {
				videoRef.current.pause();
				videoRef.current.currentTime = 0;
			}
		}, 1000);
	}, [showThumbnailOverlay, isThumbnailVideo]);

	const {
		currentShot,
		lockAutoPlay,
	} = useShots();

	const showMissingFeedsWarning = useMemo(
		() => (shiftKeyDepressed && !selected && missingFeeds),
		[shiftKeyDepressed, selected, missingFeeds],
	);

	const onThumbnailClick = () => {
		if (onSelect && !showMenu) {
			onSelect(shotId);
		}
	};

	const onThumbnailEditClick = () => {
		if (onEditClicked) {
			onEditClicked(shotId);
		}
	};

	useEffect(() => {
		// if the order has changed while the shot is playing
		// the play delay needs to be reset to sync
		setIsPlayingDelay(0);
		setAnimationHasEnded(true);

		if (playStart && selected && !clearShot) {
			setTimeout(() => {
				setIsPlayingDelay(Date.now() - (playStart?.valueOf() ?? 0));
				setAnimationHasEnded(false);
			}, 150);
		}
	}, [
		playStart,
		isDragging,
		order,
		selected,
		clearShot,
		shotDuration,
	]);

	useEffect(() => {
		if (thumbnailRef.current) setThumbnailWidth(thumbnailRef.current.clientWidth);
	}, [borderRatio]);

	useEffect(() => {
		if (hovering && showMissingFeedsWarning && shotButtonRef.current && onTooltipHover) {
			setShowingTooltip(true);
			onTooltipHover(true, missingFeeds, shotButtonRef.current);
		} else if (
			showingTooltip
			&& (!hovering || !showMissingFeedsWarning)
			&& shotButtonRef.current && onTooltipHover
		) {
			setShowingTooltip(false);
			onTooltipHover(false, missingFeeds, shotButtonRef.current);
		}
	}, [showingTooltip, hovering, showMissingFeedsWarning]);

	function marginAndBackground(usedLayout: string) {
		const background = {
			backgroundImage: backgroundType === 'image' ? `url('${backgroundImage}')` : '',
			backgroundColor: `${backgroundColor}`,
			backgroundSize: `${backgroundScale}%`,
		};

		const fixedSpacingLayouts = [...FIXED_LAYOUTS, ...PLAIN_LAYOUTS];
		const isFixedLayout = usedLayout
			? fixedSpacingLayouts.some((layout) => usedLayout.includes(layout))
			: false;
		if (isFixedLayout) {
			return {
				columnGap: 0,
				rowGap: 0,
				paddingTop: 0,
				paddingBottom: 0,
				paddingRight: 0,
				paddingLeft: 0,
				...background,
			};
		}

		const layoutMap = toMap('layout', layoutSpacingPresets);
		const layout = layoutMap.get(usedLayout);
		const backup = {
			spacing: {
				top: 0, bottom: 0, side: 0, inner: 0,
			},
		};
		const margins = layout?.spacing ? layout : backup;
		const {
			inner, top, side, bottom,
		} = margins.spacing;
		return {
			columnGap: numberToPercent(inner),
			rowGap: percMultiply(numberToPercent(inner) || '0%'),
			paddingTop: numberToPercent(top),
			paddingBottom: numberToPercent(bottom),
			paddingRight: numberToPercent(side),
			paddingLeft: numberToPercent(side),
			...background,
		};
	}

	const shotStyle = useMemo(
		() => marginAndBackground(forcedLayout),
		[
			backgroundType,
			backgroundImage,
			backgroundColor,
			backgroundScale,
			layoutSpacingPresets,
			forcedLayout,
		],
	);

	const hasPictureInPictureMemo = useMemo(
		() => forcedLayout.includes('picture-in-picture'),
		[forcedLayout],
	);

	const canSelect = useMemo((): boolean => {
		if (
			(
				shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOWBOARD
				&& roomState === ROOM_STATES.EDITING
			)
			|| (
				shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOTS_TAB
				&& roomState === ROOM_STATES.SHOWTIME
			)
		) {
			return true;
		}

		return false;
	}, [host, roomState, shotThumbnailType]);

	const layoutVideos = useCallback((selectedShotFeeds: ShotFeed[], startAt: number = 0) => {
		if (!standardFeeds) return <></>;

		return selectedShotFeeds.map((shotFeed: ShotFeed, mapIndex: number) => {
			const index = startAt + mapIndex;

			const { style, insideStyle } = getFeedStyle(
				forcedLayout,
				index,
				canvasStyle,
				true,
				thumbnailWidth,
			);

			return (
				<div
					className={classNames(`video-container-presenter ${videoGridClasses[index]}`, open && 'open')}
					key={mapIndex}
					style={style}
				>
					<FeedThumbnail
						shotFeed={shotFeed}
						forShowDetails={shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOW_DETAILS}
					/>
					{presenterVideoBorderPosition === 'inside' && (
						<div style={insideStyle} />
					)}
					{(shotFeed.presenter || shotFeed.onsiteLocationId)
					&& shotThumbnailType !== SHOT_THUMBNAIL_TYPES.SHOW_DETAILS && (
						<Title
							idOfPresenter={shotFeed.presenter}
							doAnimateThumb={selected}
							onsiteLocation={
								shotFeed.onsiteLocationId
									? onsiteLocations.find((l) => l.id === shotFeed.onsiteLocationId)
									: undefined
							}
						/>
					)}
				</div>
			);
		});
	}, [
		open,
		selected,
		presenterVideoBorderPosition,
		presenterVideoBorderSize,
		presenterVideoBorderColor,
		presenterVideoRadius,
		presenterVideoBorderOn,
		presenterVideoBorderOpacity,
		standardFeeds,
		videoTitlesOn,
		borderRatio,
		thumbnailWidth,
		onsiteLocations,
	]);

	const durationBarStyle = useMemo((): React.CSSProperties => {
		if (videoMachineState.isInitialized) {
			return {
				left: `${durationBarLeft - 1}%`,
			};
		}

		return shotDuration > 0 ? {
			animationDuration: `${shotDuration}s`,
			animationDelay: `-${isPlayingDelay}ms`,
		} : { display: 'none' };
	}, [
		isPlayingDelay,
		shotDuration,
		videoMachineState.isInitialized,
		durationBarLeft,
	]);

	useEffect(() => {
		if (videoMachineState.elapsedPercent === 100) {
			setAnimationHasEnded(true);
		}
		setDurationBarLeft(videoMachineState.elapsedPercent);
	}, [videoMachineState.elapsedPercent]);

	// Generate thumbnail if shot doesn't have one and is not empty
	useEffect(() => {
		if (!useNewShotThumbs || shot.thumbnail_url) return;
		if (!token || !meeting?.meeting_id || !meeting?.channel) return;

		const hasContent = shotFeeds?.some(
			(feed) => !feed.audioOnly && feed.feedType !== FEED_TYPES.EMPTY
		);
		if (!hasContent) return;

		GenerateShotThumbnail(
			token,
			meeting.meeting_id,
			shot.shot_id,
			meeting.channel,
			true, // bypassCache
		).catch((err) => {
			console.error(`Failed to generate thumbnail for shot ${shot.shot_id}:`, err);
		});
		shot.thumbnail_url = 'generating'; // prevent duplicate requests
	}, [shot.shot_id, shot.thumbnail_url, token, meeting?.meeting_id, meeting?.channel, shotFeeds, useNewShotThumbs]);

	const shouldShowShotContextMenu = useMemo(
		(): boolean => host
			&& open
			&& shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOWBOARD,
		[
			host,
			open,
			shotThumbnailType,
		],
	);

	const durationBar = (): JSX.Element | null => {
		if (
			(shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOWBOARD
				|| shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOTS_TAB)
			&& (shotDuration || videoMachineState.duration)
			&& !clearShot
		) {
			return (
				<div
					className={classNames(
						'duration-bar',
						playing
						&& playState !== PLAY_STATES.LATE
						&& !animationHasEnded
						&& 'playing',
					)}
					style={durationBarStyle}
					onAnimationEnd={() => {
						if (!videoMachineState.isInitialized) {
							setAnimationHasEnded(true);
						}
					}}
				/>
			);
		}
		return null;
	};

	return (
		<div className={classNames('shot-thumbnail', selected && 'selected', (roomState === ROOM_STATES.SHOWTIME && !playing) && 'pre-live')}>
			<div
				className={classNames('shot-thumbnail-inside', canSelect && 'selectable')}
				onClick={onThumbnailClick}
				onMouseEnter={handleThumbnailMouseEnter}
				onMouseLeave={handleThumbnailMouseLeave}
				ref={thumbnailRef}
			>
				{shouldShowShotContextMenu ? (
					<>
						<Portal targetId="portal-shot-context-menu">
							<ShotContextMenu
								menuPosition={menuPosition}
								isShowing={showMenu}
								onMenuBlur={() => setShowMenu(false)}
								clickInitiator={menuDotsRef}
								editHidden={roomState === ROOM_STATES.EDITING}
								isAutoplaying={autoplay}
								autoplayHidden={(lockAutoPlay && shotId === currentShot.shot_id)}
								socket={socket}
								shot={shot}
							/>
						</Portal>
						<div
							className="shot-thumbnail-dots-wrap"
							onClick={(e) => {
								if (host) {
									e.preventDefault();
									e.stopPropagation();

									if (menuDotsRef.current) {
										const pos = menuDotsRef.current.getBoundingClientRect();

										setMenuPosition({
											top: pos.top,
											left: pos.left
										});
									}

									setShowMenu((cur) => !cur);
								}
							}}
							ref={menuDotsRef}
						>
							<Icon
								name={ICONS.THREE_DOTS_HORIZONTAL}
								size={20}
								color={COLORS.WHITE}
							/>
						</div>
					</>
				) : (
					<></>
				)}
				{!showThumbnailOverlay && <DebugGate debugFeatureFlag={debugOverlaysEnabled}>
					<div className="custom-graphics-canvas-container">
						<CustomGraphicsCanvas
							renderedGraphics={shotGraphics}
							canEdit={false}
							location={
								shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOTS_TAB
									? CanvasLocation.ShotTab
									: `thumbnail-${shotId}`
							}
							isThumbnail={true}
							width={0}
							height={0}
						/>
					</div>
				</DebugGate>}
				{hasPictureInPictureMemo ? (
					<>
						<div
							style={shotStyle}
							className={classNames(
								'main-video layer1',
								forcedLayout,
							)}
						>
							{layoutVideos(standardFeeds.slice(1), 1)}
						</div>
						<div
							className={classNames(
								'main-video layer2',
								forcedLayout,
							)}
						>
							{layoutVideos(standardFeeds.slice(0, 1))}
						</div>
					</>
				) : (
					<div
						style={shotStyle}
						className={classNames('main-video', forcedLayout)}
					>
						{layoutVideos(standardFeeds)}
					</div>
				)}
				{showThumbnailOverlay && (
					<div className="shot-thumbnail-preview-overlay">
						{isThumbnailVideo ? (
							<video
								ref={videoRef}
								src={thumbnailUrl}
								loop
								muted
								playsInline
								style={{
									width: '100%',
									height: '100%',
									objectFit: 'cover',
								}}
							/>
						) : (
							<img
								src={thumbnailUrl}
								alt="Shot thumbnail"
								style={{
									width: '100%',
									height: '100%',
									objectFit: 'cover',
								}}
							/>
						)}
					</div>
				)}
				{!isDragging && (
					<div
						className={classNames(
							'shot-thumbnail-overlay',
							shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOTS_TAB
								&& roomState === ROOM_STATES.SHOWTIME && [
								'width-full',
								'height-full',
							],
							{ 'show-background': host || roomState === ROOM_STATES.EDITING },
						)}
					>
						{durationBar()}
						{host
							&& roomState === ROOM_STATES.SHOWTIME
							&& shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOWBOARD
							&& (
							<>
								<button
									className={classNames(
										'play-shot',
										showMissingFeedsWarning ? 'background-red' : 'background-green',
										{
											warning: showMissingFeedsWarning,
											open,
											'margin-right-small': (!shiftKeyDepressed || selected),
											'display-none': !open,
										},
									)}
									onClick={() => { if (onPlay) onPlay(shotId); }}
									onMouseEnter={() => setHovering(true)}
									onMouseLeave={() => setHovering(false)}
									ref={shotButtonRef}
								>
									<span>{(shiftKeyDepressed && !selected) ? 'Change Shot' : 'Shot Info'}</span>
								</button>
								{(!shiftKeyDepressed || selected) && open && (
									<button
										className={classNames('background-lighter', 'padding-small')}
										onClick={onThumbnailEditClick}
										onMouseEnter={() => setHovering(true)}
										onMouseLeave={() => setHovering(false)}
										ref={shotButtonRef}
									>
										<Icon
											name={ICONS.EDIT}
											color={showMissingFeedsWarning
												? COLORS.WHITE
												: COLORS.LIGHT_GRAY
											}
											size={14}
										/>
									</button>
								)}
								{!open && (
									<button
										className={classNames(
											'play-shot',
											showMissingFeedsWarning ? 'background-red' : 'background-green',
											{
												warning: showMissingFeedsWarning,
												open,
											},
										)}
										onClick={() => { if (onPlay) onPlay(shotId); }}
										onMouseEnter={() => setHovering(true)}
										onMouseLeave={() => setHovering(false)}
										ref={shotButtonRef}
									>
										<Icon
											name={ICONS.LIVE}
											color={showMissingFeedsWarning
												? COLORS.WHITE
												: COLORS.BLACK
											}
											size={18}
										/>
									</button>
								)}
							</>
							)}
						{host
							&& roomState === ROOM_STATES.SHOWTIME
							&& shotThumbnailType === SHOT_THUMBNAIL_TYPES.SHOTS_TAB
							&& (
							<div className='position-absolute flex-center width-full height-full position-top'>
								<button
									className={classNames('corner-round-massive', 'background-green', 'text-dark', 'thumbnail-edit-button')}
								>
									<Icon className='margin-left-small margin-right-small' name={ICONS.EDIT} size={14} color={COLORS.DEFAULT_GRAY} />
									<span className='margin-right-small'>Edit</span>
								</button>
							</div>
							)}
					</div>
				)}
			</div>
		</div>
	);
}
